/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject, signal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { MAX_FILE_SIZE_LIMIT } from '../../../../constants/general.constant';
import { getPrivacyTypeLabel, PrivacyTypeEnum } from '../../../../constants/privacy.constant';
import { ICreateGroupModel, IGroupCategoryModel, IGroupModel } from '../../../../models/groups.model';
import { IUploadPhotoAPIResponse } from '../../../../models/upload.model';
import { GroupsService } from '../../../../services/api/groups/groups.service';
import { UploadService } from '../../../../services/api/upload/upload.service';
import { GroupContextService } from '../../../../services/helper/group-context/group-context.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { CategoryPopupComponent } from '../category-popup/category-popup.component';
import { GroupInvitePopupComponent } from '../group-invite-popup/group-invite-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-create-group-popup',
    standalone: false,
    templateUrl: './create-group-popup.component.html',
    styleUrl: './create-group-popup.component.scss',
})
export class CreateGroupPopupComponent extends BasePopupComponent {
    protected PrivacyTypeEnum = PrivacyTypeEnum;
    protected getPrivacyTypeLabel = getPrivacyTypeLabel;
    protected groupImage?: File;
    protected groupImageUrl?: string;
    protected groupForm = this.fb.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
        privacy: [PrivacyTypeEnum.Public, Validators.required],
        photo: [null as number | 'pending' | null, Validators.required],
        cats: [[] as number[]],
    });
    protected groupCategories = signal<IGroupCategoryModel[]>([]);
    protected selectedGroupCategories = signal<IGroupCategoryModel[]>([]);
    protected visibleCategoryCount = 15;

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: any,
        protected override dialogRef: MatDialogRef<CreateGroupPopupComponent>,
        private fb: NonNullableFormBuilder,
        private loaderService: LoaderService,
        private groupsService: GroupsService,
        private groupContextService: GroupContextService,
        private uploadService: UploadService,
        private dialog: MatDialog,
        private router: Router,
    ) {
        super(data, dialogRef);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadGroupCategories();
    }

    protected openCategoryPopup(): void {
        const popupConfig = CategoryPopupComponent.getDialogConfig();
        popupConfig.data = {
            selectedCategories: this.selectedGroupCategories(),
        };
        const dialogRef = this.dialog.open(CategoryPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.selectedGroupCategories.set(result);
                this.groupForm.controls.cats.setValue(result.map((item: any) => item.id));
            }
        });
    }

    protected selectPrivacyType(privacy: PrivacyTypeEnum): void {
        this.groupForm.patchValue({ privacy });
    }

    protected removeGroupImage(): void {
        this.groupImage = undefined;
        this.groupImageUrl = '';
        this.groupForm.patchValue({ photo: null });
        this.groupForm.controls.photo.markAsTouched();
    }

    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > MAX_FILE_SIZE_LIMIT) {
            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
            errorPopupConfig.data = {
                title: `Error`,
                message: `File size must not exceed 10 MB.`,
            };
            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
            input.value = '';
            return;
        }
        this.groupImage = file;
        this.groupImageUrl = URL.createObjectURL(file);
        this.groupForm.patchValue({ photo: 'pending' });
    }

    protected submit(): void {
        if (this.groupForm.invalid) {
            this.groupForm.markAllAsTouched();
            return;
        }

        this.loaderService.show();

        const photoUpload$: Observable<IUploadPhotoAPIResponse | null> =
            this.groupImage && this.groupImageUrl ? this.uploadGroupImage() : of(null);

        photoUpload$
            .pipe(
                switchMap(() => {
                    return this.createGroup();
                }),
                tap((res) => {
                    const popupConfig = SuccessPopupComponent.getDialogConfig();
                    popupConfig.data = {
                        title: `New Community Gathered!`,
                        message: `<strong>${this.groupForm.controls.name.value}</strong> is ready. You can now start discussions and foster connection.`,
                        iconPath: '/assets/images/shared/success-green-icon.svg',
                        confirmButtonLabel: "Let's Go",
                    };
                    this.router.navigate(['groups', res.id]);
                    // Success popup
                    const successDialogRef = this.dialog.open(SuccessPopupComponent, popupConfig);
                    // After success popup is closed, open group invite popup
                    successDialogRef.afterClosed().subscribe(() => {
                        this.openGroupInvitePopup(res);
                        // Close the create group dialog
                        this.dialogRef.close(true);
                    });
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error creating group:', error);
                    return EMPTY;
                }),
                finalize(() => this.loaderService.hide()),
            )
            .subscribe();
    }

    protected loadGroupCategories(keyword: string = ''): void {
        this.loaderService.show();
        this.groupsService.getGroupCategoryListing(999, keyword).subscribe({
            next: (response) => {
                const selected = this.selectedGroupCategories();
                const merged = [...response];
                for (const sel of selected) {
                    if (!merged.some((cat) => cat.id === sel.id)) {
                        merged.unshift(sel); // Add selected to front if not in response
                    }
                }
                this.groupCategories.set(merged);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error getting group categories', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected toggleCategory(category: IGroupCategoryModel): void {
        const current = [...this.selectedGroupCategories()];
        const index = current.findIndex((c) => c.id === category.id);

        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(category);
        }

        this.selectedGroupCategories.set(current);
        this.groupForm.controls.cats.setValue(current.map((item) => item.id));
    }

    protected isCategorySelected(category: IGroupCategoryModel): boolean {
        return this.selectedGroupCategories().some((c) => c.id === category.id);
    }

    private uploadGroupImage(): Observable<IUploadPhotoAPIResponse> {
        const formData = new FormData();
        formData.append('file', this.groupImage!);

        return this.uploadService.uploadPhoto(formData).pipe(
            tap((res) => {
                this.groupForm.patchValue({ photo: res.id });
            }),
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on upload photo', error);
                return EMPTY;
            }),
        );
    }

    private createGroup(): Observable<IGroupModel> {
        const payload = this.groupForm.value;
        return this.groupsService.createGroup(payload as ICreateGroupModel).pipe(
            tap(() => {
                this.groupContextService.notifyMyGroupsListUpdated();
            }),
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on create group', error);
                return EMPTY;
            }),
        );
    }

    private openGroupInvitePopup(groupDetail: IGroupModel): void {
        const popupConfig = GroupInvitePopupComponent.getDialogConfig();
        popupConfig.data = { groupId: groupDetail.id, newCreatedGroupDetail: groupDetail };
        popupConfig.backdropClass = 'blurred-backdrop';
        this.dialog.open(GroupInvitePopupComponent, popupConfig);
    }
}
